import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { StatusPill, orderStatusTone } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { Pagination } from '../../components/Pagination';
import { useHasAnyPermission } from '../../lib/permissions';
import { formatBusinessDate } from '../../lib/dates';

const PAGE_SIZE = 25;

type ReservationStage = 'WAITING_DEPOSIT' | 'WAITING_STOCK' | 'READY';

interface ReservationRow {
  id: string;
  qty: number;
  status: 'PENDING' | 'HELD' | 'ALLOCATED';
  stage: ReservationStage;
  reservedAt: string;
  depositHeldAt: string | null;
  allocatedAt: string | null;
  product: { id: string; sku: string; name: string };
  location: { id: string; name: string };
  order: {
    id: string;
    number: string | null;
    status: string;
    deliveryDate: string | null;
    fulfillmentType: string;
    customer: { type: string; name: string; companyName: string | null };
    shop: { code: string; name: string };
  };
}

const STAGE_META: Record<
  ReservationStage,
  { label: string; tone: 'neutral' | 'warning' | 'positive'; hint: string }
> = {
  WAITING_DEPOSIT: { label: 'Awaiting deposit', tone: 'neutral', hint: 'Confirmed — no stock held until the deposit clears' },
  WAITING_STOCK: { label: 'Waiting for stock', tone: 'warning', hint: 'Deposit cleared — reserved against the incoming container' },
  READY: { label: 'Ready to deliver', tone: 'positive', hint: 'Stock has arrived and is allocated to this order' },
};

const STAGE_FILTERS: Array<{ value: 'all' | ReservationStage; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'READY', label: 'Ready to deliver' },
  { value: 'WAITING_STOCK', label: 'Waiting for stock' },
  { value: 'WAITING_DEPOSIT', label: 'Awaiting deposit' },
];

function shortDate(iso: string | null): string {
  return formatBusinessDate(iso);
}

export function ReservationsPage(): JSX.Element {
  const { user, perms } = useAuth();
  const isShopUser = !!user?.homeShopId && !perms.has('orders.view_all_shops');
  const canOpenOrder = useHasAnyPermission('orders.create', 'orders.view_all_shops');

  const [stage, setStage] = useState<'all' | ReservationStage>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const reservations = useQuery({
    queryKey: ['reservations'],
    queryFn: () => apiGet<ReservationRow[]>('/reservations'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const all = useMemo(() => reservations.data ?? [], [reservations.data]);
  const readyCount = useMemo(
    () => new Set(all.filter((r) => r.stage === 'READY').map((r) => r.order.id)).size,
    [all],
  );

  // Client-side stage filter + free-text search (product / SKU / customer /
  // order no.), then paginate. Mirrors StockPage — the endpoint returns the list.
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all.filter((r) => {
      if (stage !== 'all' && r.stage !== stage) return false;
      if (!ql) return true;
      return `${r.product.name} ${r.product.sku} ${r.order.customer.name} ${r.order.customer.companyName ?? ''} ${r.order.number ?? ''}`
        .toLowerCase()
        .includes(ql);
    });
  }, [all, stage, q]);

  useEffect(() => { setPage(1); }, [q, stage]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <PageScaffold
      title="Reservations"
      subtitle={
        isShopUser
          ? `Stock your shop's customers are waiting on — backorders held against incoming warehouse stock.`
          : 'Backorders held against incoming stock — what every customer is waiting on, and what has landed.'
      }
    >
      {/* Ping: stock has arrived for one or more orders → ready to deliver. */}
      {readyCount > 0 && (
        <div className="flex items-start gap-2 text-sm bg-positive-soft text-positive border border-positive/30 rounded px-3 py-2.5">
          <span className="text-base mt-px">📦</span>
          <div>
            <div className="font-medium">
              {readyCount} order{readyCount > 1 ? 's have' : ' has'} had stock arrive — ready for delivery
            </div>
            <div className="text-xs opacity-90 mt-0.5">
              Allocated automatically when the stock was received. Open each order to schedule or dispatch the delivery.
            </div>
          </div>
        </div>
      )}

      <Card bodyClassName="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="kc-input w-full sm:w-72"
            placeholder="Search product, customer or order no.…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="flex items-center gap-1 ml-auto">
            {STAGE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setStage(f.value)}
                className={
                  stage === f.value
                    ? 'px-3 py-1.5 rounded text-sm bg-accent-soft text-accent font-medium'
                    : 'px-3 py-1.5 rounded text-sm text-ink-2 hover:bg-paper-2'
                }
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card bodyClassName="p-0">
        {reservations.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No reservations"
            description={
              q.trim() || stage !== 'all'
                ? 'Nothing matches your filter.'
                : 'When an out-of-stock item is reserved against incoming stock, it shows up here.'
            }
          />
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">Product</th>
                  <th className="px-6 py-2 font-medium">Order</th>
                  <th className="px-6 py-2 font-medium">Customer</th>
                  <th className="px-6 py-2 font-medium text-right">Qty</th>
                  <th className="px-6 py-2 font-medium">Stage</th>
                  <th className="px-6 py-2 font-medium">Delivery</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const meta = STAGE_META[r.stage];
                  const since = r.depositHeldAt ?? r.reservedAt;
                  return (
                    <tr key={r.id} className="border-b border-rule-2 hover:bg-paper-2">
                      <td className="px-6 py-3">
                        <div className="text-ink">{r.product.name}</div>
                        <div className="text-xs text-ink-3 kc-mono">{r.product.sku}</div>
                      </td>
                      <td className="px-6 py-3">
                        {canOpenOrder ? (
                          <Link to={`/orders/${r.order.id}`} className="kc-mono text-ink hover:text-accent">
                            {r.order.number ?? r.order.id.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="kc-mono text-ink">{r.order.number ?? r.order.id.slice(0, 8)}</span>
                        )}
                        <div className="mt-1">
                          <StatusPill tone={orderStatusTone(r.order.status)}>{r.order.status}</StatusPill>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="text-ink">
                          {r.order.customer.type === 'company'
                            ? (r.order.customer.companyName ?? r.order.customer.name)
                            : r.order.customer.name}
                        </div>
                        {r.order.customer.type === 'company' && (
                          <div className="text-xs text-ink-3">Contact: {r.order.customer.name}</div>
                        )}
                        {!isShopUser && <div className="text-xs text-ink-3">{r.order.shop.code}</div>}
                      </td>
                      <td className="px-6 py-3 text-right kc-mono kc-tnum">{r.qty}</td>
                      <td className="px-6 py-3">
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                        <div className="text-xs text-ink-3 mt-1">
                          {meta.hint}
                          {r.stage !== 'WAITING_DEPOSIT' && <> · since {shortDate(since)}</>}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-xs text-ink-2">{shortDate(r.order.deliveryDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              page={safePage}
              pageCount={pageCount}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              unit="reservation"
            />
          </>
        )}
      </Card>
    </PageScaffold>
  );
}
